import React, {useCallback, useContext, useMemo, useState} from 'react';

import {FileInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';
import {AddedFile} from 'src/modules/create-user/user-bulk-import/steps/upload-file-step/user-import-file-upload/added-files';
import {syncAccountUsersCrmId} from 'src/openapi/admin/apis/AccountUsersCrmIdSyncApi';
import {CrmEntityType as CrmEntityTypes} from 'src/openapi/admin/models/CrmEntityType';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';

import type {CrmEntityType} from 'src/openapi/admin/models/CrmEntityType';
import type {CrmSyncType} from 'src/openapi/admin/models/CrmSyncType';

type Props = {
    accountId: number,
    crmType: CrmSyncType,
    onClose: () => void,
};

export const BulkSyncModal = ({accountId, crmType, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {setSuccessMessage} = useContext(MessageContext);

    const [entityType, setEntityType] = useState<CrmEntityType>(CrmEntityTypes.ACCOUNT);
    const [addedFile, setAddedFile] = useState<File | null>(null);

    const [uploadFileError, setUploadFileError] = useState<string>('');
    const [submitBusy, setSubmitBusy] = useState(false);
    const [disabled, setDisabled] = useState(true);

    const onDrop = useCallback((accepted: Array<File>) => {
        const loadedFile = accepted[0];
        if (loadedFile) {
            setAddedFile(loadedFile);
            setDisabled(false);
            setUploadFileError('');
        }
    }, []);

    const onSync = useCallback(async () => {
        setSubmitBusy(true);
        if (addedFile) {
            const syncResponse = await apiDispatch(syncAccountUsersCrmId({
                accountId,
                pbIds: addedFile,
                crmType,
                entityType,
            }));

            if (syncResponse.payload.success) {
                onClose();
                setSuccessMessage('Entities successfully synced');
            } else {
                setDisabled(true);
                setUploadFileError(getError({error: syncResponse.payload.data, separator: '\n'}));
            }
        }
        setSubmitBusy(false);
    }, [setUploadFileError, addedFile, accountId, crmType, entityType, apiDispatch, onClose, setSuccessMessage]);

    const addedFilesView = useMemo(() => {
        return (addedFile && !uploadFileError
            ? (
                    <AddedFile
                        width={550}
                        addedFile={addedFile}
                        removeFile={() => {
                            setAddedFile(null);
                        }}
                    />
                )
            : null
        );
    }, [addedFile, uploadFileError]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Bulk Sync
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v">
                    <WidthItem width="100%">
                        <Label>Entity Type</Label>
                        <ComboBox
                            value={entityType}
                            onChange={setEntityType}
                            name="entity-type">
                            <ComboboxItem
                                key={CrmEntityTypes.ACCOUNT}
                                {...{
                                    value: CrmEntityTypes.ACCOUNT,
                                    caption: 'Company',
                                }}
                            />
                            <ComboboxItem
                                key={CrmEntityTypes.LEAD}
                                {...{
                                    value: CrmEntityTypes.LEAD,
                                    caption: 'Lead',
                                }}
                            />
                            <ComboboxItem
                                key={CrmEntityTypes.CONTACT}
                                {...{
                                    value: CrmEntityTypes.CONTACT,
                                    caption: 'Contact',
                                }}
                            />
                        </ComboBox>
                    </WidthItem>
                    <WidthItem width="100%">
                        <Label>Upload PB IDs file</Label>
                        <Group direction="v">
                            <FileInput
                                acceptedMimeTypes={[
                                    'text/csv',
                                    'application/vnd.ms-excel',
                                ]}
                                multiple={false}
                                description="The file should contain the list of PBIDs to sync"
                                acceptedFormats={['CSV']}
                                onDrop={onDrop}
                                addedFile={addedFile}
                            />
                            {addedFilesView}
                            {uploadFileError ? (
                                <Message
                                    iconProps={{type: Icons.ERROR, size: Sizes.Size_14}}
                                    color="error"
                                    onClose={() => setUploadFileError('')}>
                                    {uploadFileError}
                                </Message>
                            ) : null}
                        </Group>
                    </WidthItem>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" onClick={onSync} busy={submitBusy} disabled={disabled}>
                        Sync
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
